import { verifyAdmin } from './admin/access';
import {
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  doc,
  query,
  where,
} from "./leagueStore";
import {
  challengesReference,
  currYear,
  getWeek,
  passedDue,
  picksReference,
  rankingsReference,
  getId,
  usersReference,
} from "./App";
import { db } from "./Firebase";
import { readOnlyPreview } from "./leagueStore";
import { challengeBank } from "./challengeBank";
import { challengesSchedule2026 } from "./challengesSchedule2026";
import {
  DECISION_ACCEPTED,
  DECISION_DECLINED,
  DECISION_PENDING,
  canOfferDecision,
  canSettle,
  publicDecisionLabel,
} from "./rules/challengeRules";
import { isPassedDue as weekIsPassedDue } from "./rules/weekLock";

function bankById() {
  const map = {};
  for (const entry of challengeBank) {
    map[entry.id] = entry;
  }
  return map;
}

function scheduleForYear(year) {
  if (year === challengesSchedule2026.year) {
    return challengesSchedule2026;
  }
  return null;
}

function publicDecisionLabelFromChallenge(challenge) {
  return publicDecisionLabel(typeof challenge === "string" ? challenge : challenge?.Decision);
}

/** Resolve schedule+bank entry for a week in the current year. */
export function getChallengeForWeek(week) {
  const schedule = scheduleForYear(currYear);
  if (!schedule) return null;

  const weekRow = schedule.weeks.find((w) => w.week === week);
  if (!weekRow) return null;

  const entry = bankById()[weekRow.bankId];
  if (!entry) return null;

  return {
    Year: currYear,
    Week: week,
    Tid: weekRow.tid,
    RecipientName: weekRow.recipientName,
    Text: entry.text,
    Faab: entry.faab,
    BankId: entry.id,
    Decision: DECISION_PENDING,
    Outcome: "",
    DecidedAt: "",
    SettledAt: "",
  };
}

/**
 * Load Firestore challenge for year+week, or create from schedule+bank if missing.
 * Returns { id, ...fields } or null if no schedule for that week.
 */
export async function ensureChallengeDoc(week) {
  const year = currYear;
  const q = query(
    challengesReference,
    where("Year", "==", year),
    where("Week", "==", week)
  );
  const data = await getDocs(q);

  if (!data.empty) {
    const d = data.docs[0];
    return { id: d.id, ...d.data() };
  }

  const fromSchedule = getChallengeForWeek(week);
  if (!fromSchedule) return null;
  if (readOnlyPreview) return { id: `preview-${year}-${week}`, ...fromSchedule };

  const ref = await addDoc(challengesReference, fromSchedule);
  return { id: ref.id, ...fromSchedule };
}

/**
 * After a weekly submit: return challenge if this team is the recipient,
 * decision is still pending, and the week is not locked.
 */
export async function offerAfterSubmit(teamId, week) {
  if (!teamId || week < 1) return null;
  if (weekIsPassedDue(Date.now(), week)) return null;

  const challenge = await ensureChallengeDoc(week);
  if (!challenge) return null;

  if (!canOfferDecision(challenge, teamId, false, true)) return null;

  return challenge;
}

export async function acceptChallenge(challengeId) {
  await validateDecision(challengeId);
  const challengeDoc = doc(db, "Challenges", challengeId);
  await updateDoc(challengeDoc, {
    Decision: DECISION_ACCEPTED,
    DecidedAt: new Date().toISOString(),
  });
}

export async function declineChallenge(challengeId) {
  await validateDecision(challengeId);
  const challengeDoc = doc(db, "Challenges", challengeId);
  await updateDoc(challengeDoc, {
    Decision: DECISION_DECLINED,
    DecidedAt: new Date().toISOString(),
  });
}

async function validateDecision(challengeId) {
  const [snapshot, tid] = await Promise.all([
    getDoc(doc(db, "Challenges", challengeId)), getId(usersReference),
  ]);
  const challenge = snapshot.data();
  if (!snapshot.exists() || challenge.Tid !== tid || challenge.Decision !== DECISION_PENDING) {
    throw new Error("This challenge is no longer available for a decision.");
  }
  if (challenge.Year !== currYear || weekIsPassedDue(Date.now(), challenge.Week)) {
    throw new Error("Challenge decisions closed Thursday at 7:30 p.m. CT.");
  }
}

export async function listChallengesForYear(year = currYear) {
  const q = query(challengesReference, where("Year", "==", year));
  const data = await getDocs(q);
  const list = data.docs.map((d) => ({ id: d.id, ...d.data() }));
  list.sort((a, b) => (a.Week || 0) - (b.Week || 0));
  return list;
}

export async function settleChallenge(challengeId, outcome) {
  const normalized = String(outcome || "").trim().toLowerCase();
  if (normalized !== "completed" && normalized !== "failed") {
    throw new Error("Outcome must be completed or failed.");
  }
  await verifyAdmin();
  const challengeDoc = doc(db, "Challenges", challengeId);
  const snapshot = await getDoc(challengeDoc);
  const challenge = snapshot.data();
  if (!snapshot.exists() || challenge.Year !== currYear || !canSettle(challenge, true, getWeek())) {
    throw new Error("Only accepted, unsettled challenges from a completed week can be reviewed.");
  }
  await updateDoc(challengeDoc, {
    Outcome: normalized,
    SettledAt: new Date().toISOString(),
  });
}

export function canSettleChallenge(challenge, isAdmin, currentWeek) {
  return canSettle(challenge, isAdmin, currentWeek);
}

export {
  publicDecisionLabelFromChallenge as publicDecisionLabel,
  DECISION_PENDING,
  DECISION_ACCEPTED,
  DECISION_DECLINED,
};

/** Pending offer for signed-in team on current week (reopen before lock). */
export async function getPendingOfferForTeam(teamId) {
  if (!teamId || passedDue()) return null;
  const week = getWeek();

  const picksQ = query(
    picksReference,
    where("Week", "==", week),
    where("Tid", "==", teamId),
    where("Year", "==", currYear)
  );
  const rankingsQ = query(
    rankingsReference,
    where("Week", "==", week),
    where("Tid", "==", teamId),
    where("Year", "==", currYear)
  );
  const [picks, rankings] = await Promise.all([
    getDocs(picksQ),
    getDocs(rankingsQ),
  ]);
  if (picks.empty && rankings.empty) return null;

  return offerAfterSubmit(teamId, week);
}
